// Single row in the Action Inbox list.
// Shows code, title, priority badge, due date, owner, status. Rendered as
// a non-interactive div — the parent wraps it in a <Link> so the whole
// row is clickable and navigates to /actions/:code. The StatusPill inside
// intercepts the click so users can advance state inline without navigating.

import { useState } from "react";
import { Flag, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/cn";
import { ActionSourceChip } from "@/components/ActionSourceChip";
import { LastEditedBy } from "@/components/LastEditedBy";
import { StatusPill } from "@/components/StatusPill";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { mcp, type ActionStatus } from "@/lib/mcp";
import { useIsAdmin } from "@/hooks/useIsAdmin";

interface ActionRowProps {
  action: Record<string, unknown>;
  onChanged?: () => void;
}

const PRIORITY_TONES: Record<string, string> = {
  critical: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  urgent: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200",
  high: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  normal: "bg-muted text-muted-foreground",
  low: "bg-muted/50 text-muted-foreground",
};

function fmtDate(d: unknown): string {
  if (!d) return "—";
  const date = new Date(String(d));
  if (Number.isNaN(date.getTime())) return String(d);
  // Compact format: dates within 7 days drop year/details, older dates show
  // year only when the year differs from "now".
  const now = new Date();
  const diffMs = Math.abs(now.getTime() - date.getTime());
  const sameYear = date.getFullYear() === now.getFullYear();
  if (sameYear || diffMs <= 7 * 86400_000) {
    return date.toLocaleDateString("en-ZA", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-ZA", { year: "numeric", month: "short", day: "numeric" });
}

export function ActionRow({ action, onChanged }: ActionRowProps) {
  const code = String(action.code ?? action.id ?? "—");
  const title = String(action.title ?? "(untitled)");
  const priority = String(action.priority ?? "normal");
  const owner = String(action.owner_label ?? action.owner ?? "Unassigned");
  const status = String(action.status ?? "open") as ActionStatus;
  const isFlagged = Boolean(action.is_josh_flagged);
  const { isAdmin } = useIsAdmin();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDeleting(true);
    try {
      await mcp.deleteAction({ code });
      toast.success(`Deleted ${code}`);
      setConfirmDelete(false);
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      className={cn(
        "group relative rounded-md border bg-card p-3 text-sm shadow-sm transition-colors",
        "hover:bg-muted/40 hover:border-primary/50",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground">{code}</span>
            {isFlagged && <Flag className="h-3.5 w-3.5 text-orange-500" />}
            <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium uppercase", PRIORITY_TONES[priority] ?? PRIORITY_TONES.normal)}>
              {priority}
            </span>
            <StatusPill code={code} status={status} onAdvanced={onChanged} />
            <ActionSourceChip source={action.source as string | null | undefined} />
          </div>
          <p className="mt-1 truncate font-medium">{title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {owner} · due {fmtDate(action.due_date)}
          </p>
        </div>
        <div className="flex items-start gap-1">
          {isAdmin && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDelete(true); }}
              aria-label={`Delete ${code}`}
              title="Delete (admin only)"
              className="rounded-md p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <LastEditedBy
            updated_at={(action.updated_at as string | null | undefined) ?? null}
            updated_by={(action.updated_by as string | null | undefined) ?? null}
            agent_id={(action.agent_id as string | null | undefined) ?? null}
          />
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {code}?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes the action from every list and meeting cascade. Audit history stays.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { void handleDelete(e); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (<><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> Deleting</>) : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
